import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Loader2,
  AlertCircle,
  User,
  Briefcase,
  Clock,
  Download,
  Copy,
  Check
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface TranscriptSegment {
  participant: string;
  role: string;
  text: string;
  startTime: number;
  endTime: number;
  confidence: number;
}

interface TranscriptionJob {
  status: string;
  media_duration_seconds: number;
  words_transcribed: number;
  speakers_detected: number;
  completed_at: string;
  error?: string;
}

interface TranscriptData {
  interviewId: string;
  transcriptStatus: string;
  analysisStatus: string;
  fullTranscript: string;
  segments: TranscriptSegment[];
  job: TranscriptionJob | null;
}

interface TranscriptViewerModalProps {
  interviewId: string;
  expertName: string;
  open: boolean;
  onClose: () => void;
}

export function TranscriptViewerModal({
  interviewId,
  expertName,
  open,
  onClose
}: TranscriptViewerModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transcriptData, setTranscriptData] = useState<TranscriptData | null>(null);
  const [copiedSegment, setCopiedSegment] = useState<number | null>(null);
  const [copiedFull, setCopiedFull] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTranscript();
    }
  }, [open, interviewId]);

  const fetchTranscript = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/api/interviews/${interviewId}/transcription`);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch transcript');
      }

      const data = await response.json();
      setTranscriptData(data);
    } catch (err) {
      console.error('Error fetching transcript:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transcript');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  const handleCopySegment = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSegment(index);
      setTimeout(() => setCopiedSegment(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyFull = async () => {
    if (!transcriptData?.fullTranscript) return;

    try {
      await navigator.clipboard.writeText(transcriptData.fullTranscript);
      setCopiedFull(true);
      setTimeout(() => setCopiedFull(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    if (!transcriptData?.fullTranscript) return;

    const blob = new Blob([transcriptData.fullTranscript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interview-${expertName.replace(/\s+/g, '-').toLowerCase()}-transcript.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getSpeakerIcon = (role: string) => {
    return role === 'journalist' ? User : Briefcase;
  };

  const getSpeakerColor = (role: string) => {
    return role === 'journalist' ? 'text-blue-600' : 'text-purple-600';
  };

  const getSpeakerBgColor = (role: string) => {
    return role === 'journalist' ? 'bg-blue-50' : 'bg-purple-50';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Interview Transcript</DialogTitle>
          <DialogDescription>
            Transcript for interview with {expertName}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : transcriptData ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Stats Bar */}
            {transcriptData.job && (
              <div className="grid grid-cols-4 gap-4 mb-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Duration</div>
                    <div className="text-2xl font-bold">
                      {formatDuration(transcriptData.job.media_duration_seconds)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Words</div>
                    <div className="text-2xl font-bold">
                      {transcriptData.job.words_transcribed?.toLocaleString()}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Speakers</div>
                    <div className="text-2xl font-bold">
                      {transcriptData.job.speakers_detected}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Segments</div>
                    <div className="text-2xl font-bold">{transcriptData.segments.length}</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Transcript Tabs */}
            <Tabs defaultValue="segmented" className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <TabsList>
                  <TabsTrigger value="segmented">Segmented View</TabsTrigger>
                  <TabsTrigger value="full">Full Transcript</TabsTrigger>
                </TabsList>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              {/* Segmented View */}
              <TabsContent value="segmented" className="flex-1 overflow-y-auto space-y-3 mt-0">
                {transcriptData.segments.map((segment, index) => {
                  const Icon = getSpeakerIcon(segment.role);
                  return (
                    <Card
                      key={index}
                      className={`${getSpeakerBgColor(segment.role)} border-l-4 ${
                        segment.role === 'journalist' ? 'border-l-blue-500' : 'border-l-purple-500'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            <div
                              className={`w-8 h-8 rounded-full ${getSpeakerBgColor(
                                segment.role
                              )} flex items-center justify-center flex-shrink-0`}
                            >
                              <Icon className={`w-4 h-4 ${getSpeakerColor(segment.role)}`} />
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`font-semibold ${getSpeakerColor(segment.role)}`}>
                                  {segment.participant}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {formatTime(segment.startTime)}
                                </Badge>
                                {segment.confidence && (
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${
                                      segment.confidence > 0.9
                                        ? 'text-green-600'
                                        : segment.confidence > 0.7
                                        ? 'text-yellow-600'
                                        : 'text-orange-600'
                                    }`}
                                  >
                                    {Math.round(segment.confidence * 100)}% confidence
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm leading-relaxed">{segment.text}</p>
                            </div>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopySegment(segment.text, index)}
                          >
                            {copiedSegment === index ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </TabsContent>

              {/* Full Transcript View */}
              <TabsContent value="full" className="flex-1 overflow-hidden flex flex-col mt-0">
                <Card className="flex-1 overflow-hidden flex flex-col">
                  <CardContent className="p-4 flex-1 overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Complete Transcript</h3>
                      <Button variant="outline" size="sm" onClick={handleCopyFull}>
                        {copiedFull ? (
                          <>
                            <Check className="w-4 h-4 mr-2 text-green-600" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy All
                          </>
                        )}
                      </Button>
                    </div>
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                        {transcriptData.fullTranscript}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
